import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  ArrowLeftIcon,
  CopyIcon,
  EditIcon,
  DownloadIcon,
  XIcon,
  CheckIcon,
  HistoryIcon,
  UsersIcon,
  RefreshCwIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Navbar } from "../../components/common/Navbar";
import { AssessmentForm } from "../../components/assessment-builder/AssessmentForm";
import { EnhancePromptDialog } from "../../components/assessment-builder/EnhancePromptDialog";
import { InviteCollaboratorDialog } from "../../components/collaboration";
import { useAuth } from "../../context/AuthContext";
import {
  updateChallengeProgress,
  saveAssessment,
  updateAssessment,
  Assessment,
} from "../../lib/supabase";
import { exportResultToPDF } from "../../lib/exportPdf";
import { CommentSectionAssessment } from "../../components/comment/CommentSectionAssessment";
import {
  validateAssessmentRequest,
  getDefaultCognitiveDistribution,
  getDefaultQuestionCount,
  CognitiveDistribution,
} from "../../lib/assessmentService";
import { getUnifiedAIService } from "../../lib/unifiedAIService";
import { supabase } from "../../lib/supabase";
import { MarkdownRenderer } from "../../components/ui/MarkdownRenderer";
import { marked } from "marked";
import { GenerateLoadingModal } from "../../components/common/GenerateLoadingModal";

type ViewMode = "input" | "result" | "edit";

export const AssessmentBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUserProfile } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    mataPelajaran: "",
    jenjangPendidikan: "",
    kelas: "",
    topik: "",
    tujuanPembelajaran: "",
    tipePertanyaan: "",
    kriteriaTambahan: "",

    // Enhanced fields
    jumlahSoal: 10,
    tingkatKesulitan: "campuran",
    distribusiKognitif: {
      c1c2: 50,
      c3: 30,
      c4c5c6: 20,
    } as CognitiveDistribution,
    includeAnswerKey: true,
    includeRubric: false,
    aloksiWaktu: 60,
  });

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>("input");

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<string | null>(null);
  const [generatedTitle, setGeneratedTitle] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState<string>("");

  // Edit state
  const [editableResult, setEditableResult] = useState<string>("");
  const [editableTitle, setEditableTitle] = useState<string>("");
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  // Dialog state
  const [showEnhancePromptDialog, setShowEnhancePromptDialog] = useState(false);
  const [showInviteCollaboratorDialog, setShowInviteCollaboratorDialog] =
    useState(false);

  // Animation state for content transitions
  const [isTransitioning, setIsTransitioning] = useState(false);

  // TAMBAHAN: State untuk Comment Section
  const [CurrentAssessmentId, setCurrentAssessmentId] = useState<string | null>(
    null
  );

  const [isCommentsLoading, setIsCommentsLoading] = useState(true);

  // Handle editing from history
  useEffect(() => {
    if (location.state && "assessment" in location.state) {
      const assessment = location.state.assessment as Assessment;

      // Populate form data - split combined topik_tujuan field
      const topikTujuanParts = assessment.topik_tujuan.split(" - ");
      const topik = topikTujuanParts[0] || "";
      const tujuanPembelajaran = topikTujuanParts.slice(1).join(" - ") || "";

      // Populate form data
      setFormData({
        mataPelajaran: assessment.mata_pelajaran,
        jenjangPendidikan: assessment.jenjang_pendidikan,
        kelas: "",
        topik: topik,
        tujuanPembelajaran: tujuanPembelajaran,
        tipePertanyaan: assessment.tipe_pertanyaan,
        kriteriaTambahan: assessment.kriteria_tambahan || "",
        // Enhanced fields with defaults
        jumlahSoal: getDefaultQuestionCount(assessment.tipe_pertanyaan),
        tingkatKesulitan: "campuran",
        distribusiKognitif: getDefaultCognitiveDistribution(),
        includeAnswerKey: true,
        includeRubric: assessment.tipe_pertanyaan
          .toLowerCase()
          .includes("uraian"),
        aloksiWaktu: 60,
      });

      setIsCommentsLoading(true);
      // Set generated content
      setGeneratedTitle(assessment.title);
      setGeneratedResult(assessment.content);
      setEditableTitle(assessment.title);
      setEditableResult(assessment.content);

      // Set to edit mode
      setViewMode("edit");
      setEditingItemId(assessment.id);

      // TAMBAHAN: Set assessment ID untuk comment section
      setCurrentAssessmentId(assessment.id);

      // Clear the location state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state && "fromLessonPlan" in location.state) {
      const lessonPlanData = location.state;

      // Validate required fields from lesson plan
      const requiredFields = [
        "mataPelajaran",
        "jenjangPendidikan",
        "topik",
        "tujuanPembelajaran",
      ];
      const missingFields = requiredFields.filter(
        field => !lessonPlanData[field]?.trim()
      );

      if (missingFields.length > 0) {
        toast.error(`Data tidak lengkap: ${missingFields.join(", ")}`);
        console.warn("Missing fields from lesson plan:", missingFields);
        return;
      }

      // Populate form with transferred data from lesson plan
      setFormData(prev => ({
        ...prev,
        mataPelajaran: lessonPlanData.mataPelajaran || "",
        jenjangPendidikan: lessonPlanData.jenjangPendidikan || "",
        kelas: lessonPlanData.kelas || "",
        topik: lessonPlanData.topik || "",
        tujuanPembelajaran: lessonPlanData.tujuanPembelajaran || "",
        kriteriaTambahan: lessonPlanData.kriteriaTambahan || "",
        // Set defaults based on transferred data
        tipePertanyaan: "", // Reset tipePertanyaan, harus diisi user
        distribusiKognitif: getDefaultCognitiveDistribution(),
        jumlahSoal: 10,
        tingkatKesulitan: "campuran",
        includeAnswerKey: true,
        includeRubric: false,
        aloksiWaktu: 60,
      }));

      // Clear navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);

      // Show success message with details
      const hasKriteria = lessonPlanData.kriteriaTambahan?.trim();
      toast.success(
        hasKriteria
          ? "Data lesson plan dan konten berhasil ditransfer!"
          : "Data lesson plan berhasil ditransfer!"
      );
    }
  }, [location.state]);

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  const handleGoToHistory = () => {
    navigate("/history");
  };

  const handleInputChange = (
    field: string,
    value: string | number | boolean | CognitiveDistribution
  ) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Auto-update defaults when jenjang or tipe changes
      if (field === "jenjangPendidikan" && typeof value === "string") {
        newData.distribusiKognitif = getDefaultCognitiveDistribution();
        // Reset kelas ketika jenjang berubah
        newData.kelas = "";
      }

      if (field === "tipePertanyaan" && typeof value === "string") {
        newData.jumlahSoal = getDefaultQuestionCount(value);
        // Auto-enable rubric for essay questions
        newData.includeRubric =
          value.toLowerCase().includes("uraian") ||
          value.toLowerCase().includes("essay");
      }

      return newData;
    });
  };

  const handleEnhancePrompt = () => {
    setShowEnhancePromptDialog(true);
  };

  const handleCloseEnhancePromptDialog = () => {
    setShowEnhancePromptDialog(false);
  };

  const handleAcceptEnhancedPrompt = (enhancedObjectives: string) => {
    // Parse the enhanced objectives into a list
    const objectivesList = enhancedObjectives
      .split("\n")
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.match(/^\d+\.$/)) // Remove empty lines and standalone numbers
      .map(line => line.replace(/^\d+\.\s*/, "")) // Remove leading numbers and dots
      .filter(line => line.length > 0);

    // Join the objectives with " - " separator for the tujuanPembelajaran field
    const objectivesString = objectivesList.join(" - ");

    setFormData(prev => ({
      ...prev,
      tujuanPembelajaran: objectivesString,
    }));

    toast.success("Tujuan pembelajaran berhasil ditingkatkan!");
  };

  // 🆕 FUNGSI BARU: Untuk mengupdate progress dan notifikasi challenge (Opsional/Background)
  const updateChallengeAndProfile = useCallback(async () => {
    const { getCurrentUser } = await import("../../lib/authUtils");
    const { user: currentUser, error: authError } = await getCurrentUser();

    if (authError || !currentUser) {
      return;
    }

    try {
      const challengeResult = await updateChallengeProgress(
        currentUser.id,
        "assessment_created"
      );

      if (challengeResult.success && challengeResult.data) {
        await refreshUserProfile();

        if (
          challengeResult.data.challenge_completed &&
          challengeResult.data.points_awarded > 0
        ) {
          const challengeNames =
            challengeResult.data.completed_challenges.join(", ");
          toast.success(
            `Tantangan "${challengeNames}" selesai! +${challengeResult.data.points_awarded} poin`,
            { duration: 5000 }
          );
        }
      }
    } catch (error) {
      console.error("💥 Exception updating challenge progress:", error);
    }
  }, [refreshUserProfile]);

  // 🆕 FUNGSI UTAMA BARU: Menyimpan Assessment ke Database secara Synchronous (Awaitable)
  const saveAssessmentToDb = async (
    title: string,
    content: string
  ): Promise<string | undefined> => {
    const { getCurrentUser } = await import("../../lib/authUtils");
    const { user: currentUser, error: authError } = await getCurrentUser();

    if (authError || !currentUser) {
      console.error("User not authenticated for saving.");
      return;
    }

    const dataToSave = {
      title: title,
      content: content,
      mata_pelajaran: formData.mataPelajaran,
      jenjang_pendidikan: formData.jenjangPendidikan,
      // PERBAIKAN: Menggabungkan topik dan tujuan pembelajaran
      topik_tujuan: `${formData.topik} - ${formData.tujuanPembelajaran}`,
      tipe_pertanyaan: formData.tipePertanyaan,
      kriteria_tambahan: formData.kriteriaTambahan,
    };

    if (content.length < 50) {
      console.error("Content is too short, aborting save to database.");
      throw new Error("Konten hasil generate terlalu pendek (gagal dari AI).");
    }

    try {
      const saveResult = await saveAssessment(currentUser.id, dataToSave);

      if (saveResult.success && saveResult.data?.id) {
        return saveResult.data.id;
      } else {
        console.error("❌ Failed to save assessment:", saveResult.error);
        throw new Error(
          saveResult.error || "Gagal menyimpan soal assessment ke database."
        );
      }
    } catch (error) {
      console.error("💥 Exception saving assessment:", error);
      throw error;
    }
  };

  const handleGenerateNow = async ({
    isRegenerate = false,
  }: { isRegenerate?: boolean } = {}) => {
    // Validate form data - combine fields for validation
    const validationData = {
      ...formData,
      topikTujuan: `${formData.topik} - ${formData.tujuanPembelajaran}`,
      tingkatKesulitan: formData.tingkatKesulitan as
        | "mudah"
        | "sedang"
        | "sulit"
        | "campuran",
    };

    // 1. VALIDASI
    const wasEditMode = viewMode === "edit";
    const kelasInvalid = !formData.kelas;
    // Di mode edit, jangan wajibkan kelas
    if (
      (kelasInvalid && !wasEditMode) ||
      !validateAssessmentRequest(validationData)
    ) {
      toast.error("Mohon lengkapi semua field yang diperlukan");
      return;
    }

    // 2. SETUP LOADING STATE (Initial)
    setIsGenerating(true);
    setGeneratedResult(null);
    setGeneratedTitle(null);
    // Jangan kosongkan editor saat sedang di mode edit
    if (!wasEditMode) {
      setEditableResult("");
      setEditableTitle("");
    }
    setProgress(0);
    setIsStreaming(false);
    setStreamedContent("");
    const existingAssessmentId = CurrentAssessmentId;
    if (!isRegenerate) {
      setCurrentAssessmentId(null); // RESET ID untuk generate baru
      setEditingItemId(null);
    }
    // Tetap di mode edit jika regenerate dari edit
    if (!wasEditMode) {
      setViewMode("result"); // Switch to result mode untuk non-edit
    }

    // Handle scroll behavior (sama seperti sebelumnya)
    setTimeout(() => {
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      if (isMobile) {
        // Scroll to bottom on mobile
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        });
      } else {
        // Scroll to top on desktop
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 100);

    let progressInterval: NodeJS.Timeout | null = null;
    const aiService = getUnifiedAIService();
    const providerStatus = aiService.getProviderStatus();

    try {
      let finalContent: string | null = null;
      let finalTitle: string | null = null;
      let finalProvider: string = "AI";
      let isFallback = false;

      // Start progress simulation (for non-streaming)
      if (!providerStatus.streamingEnabled) {
        progressInterval = setInterval(() => {
          setProgress(prev => {
            if (prev >= 85) {
              if (progressInterval) clearInterval(progressInterval);
              return 85;
            }
            return prev + Math.random() * 10;
          });
        }, 300);
      }

      // 3. PANGGIL AI SERVICE
      const result = await aiService.generateAssessment({
        mataPelajaran: formData.mataPelajaran,
        // Sertakan kelas ke dalam konteks jenjang untuk AI
        jenjangPendidikan: formData.kelas
          ? `${formData.jenjangPendidikan} ${formData.kelas}`
          : formData.jenjangPendidikan,
        topikTujuan: `${formData.topik} - ${formData.tujuanPembelajaran}`,
        tipePertanyaan: formData.tipePertanyaan,
        kriteriaTambahan: formData.kriteriaTambahan,
        jumlahSoal: formData.jumlahSoal,
        tingkatKesulitan: formData.tingkatKesulitan,
        distribusiKognitif: formData.distribusiKognitif,
        includeAnswerKey: formData.includeAnswerKey,
        includeRubric: formData.includeRubric,
        aloksiWaktu: formData.aloksiWaktu,
      });

      // Clear progress interval if it was set
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      // 4. LOGIKA FINAL SETELAH AI SELESAI
      if (result.success) {
        // Tentukan konten dan judul final
        finalContent = finalContent || result.content || null;
        finalTitle =
          finalTitle ||
          result.title ||
          generatedTitle ||
          `Soal ${formData.mataPelajaran} - ${(formData.kelas
            ? `${formData.jenjangPendidikan} ${formData.kelas}`
            : formData.jenjangPendidikan
          )?.toUpperCase()} (${formData.tipePertanyaan})`;
        finalProvider = result.provider || finalProvider;
        isFallback = isFallback || result.fallback || false;

        if (!finalContent) {
          throw new Error("Konten hasil generate kosong.");
        }

        const sanitizedTitle = finalTitle?.trim() || "";
        const sanitizedContent = finalContent.trim();
        if (!sanitizedTitle || sanitizedContent.length < 50) {
          throw new Error("Konten hasil generate tidak valid untuk disimpan.");
        }

        let targetAssessmentId: string | undefined =
          existingAssessmentId ?? undefined;
        let shouldUpdateChallenges = false;

        if (isRegenerate && existingAssessmentId) {
          const { getCurrentUser } = await import("../../lib/authUtils");
          const { user: currentUser, error: authError } =
            await getCurrentUser();

          if (authError || !currentUser) {
            throw new Error("Sesi tidak valid. Silakan login kembali.");
          }

          const updatePayload = {
            title: sanitizedTitle,
            content: sanitizedContent,
            mata_pelajaran: formData.mataPelajaran,
            jenjang_pendidikan: formData.jenjangPendidikan,
            topik_tujuan: `${formData.topik} - ${formData.tujuanPembelajaran}`,
            tipe_pertanyaan: formData.tipePertanyaan,
            kriteria_tambahan: formData.kriteriaTambahan,
          };

          const updateResult = await updateAssessment(
            currentUser.id,
            existingAssessmentId,
            updatePayload
          );

          if (!updateResult.success) {
            throw new Error(
              updateResult.error ||
                "Gagal memperbarui soal assessment setelah regenerasi."
            );
          }
        } else {
          const newAssessmentId = await saveAssessmentToDb(
            sanitizedTitle,
            sanitizedContent
          );

          if (!newAssessmentId) {
            throw new Error(
              "Gagal mendapatkan ID setelah penyimpanan database."
            );
          }

          targetAssessmentId = newAssessmentId;
          shouldUpdateChallenges = true;
        }

        if (targetAssessmentId) {
          setCurrentAssessmentId(targetAssessmentId);
          setEditingItemId(targetAssessmentId);

          // Set state tampilan final
          setGeneratedTitle(sanitizedTitle);
          setGeneratedResult(sanitizedContent);
          setEditableTitle(sanitizedTitle);
          setEditableResult(sanitizedContent);

          // Set generating to false immediately after successful generation
          setIsGenerating(false);

          // Pindah ke tampilan hasil hanya jika bukan dari mode edit
          if (!wasEditMode) {
            setViewMode("result");
          }

          if (shouldUpdateChallenges) {
            updateChallengeAndProfile();
          }

          // Tampilkan toast sukses jika non-streaming (toast streaming sudah di handle di onStreamComplete)
          if (!providerStatus.streamingEnabled) {
            const toastMessage = isFallback
              ? `Soal assessment berhasil dibuat menggunakan template!`
              : `Soal assessment berhasil dibuat dengan ${
                  finalProvider === "sumpod-openai"
                    ? "SumoPod AI"
                    : finalProvider
                }!`;
            toast.success(toastMessage, { duration: 4000 });
          }
        }
      } else {
        throw new Error(result.error || "Failed to generate assessment");
      }
    } catch (error) {
      // Send error to Sentry with context
      Sentry.captureException(error, {
        tags: {
          component: "AssessmentBuilder",
          action: "handleGenerateNow",
        },
        contexts: {
          form_data: {
            mataPelajaran: formData.mataPelajaran,
            jenjangPendidikan: formData.jenjangPendidikan,
            kelas: formData.kelas,
            topik: formData.topik,
            tipePertanyaan: formData.tipePertanyaan,
            tingkatKesulitan: formData.tingkatKesulitan,
          },
        },
      });
      
      setProgress(0);
      setIsStreaming(false);
      setStreamedContent("");
      setCurrentAssessmentId(null); // Pastikan ID di-reset jika gagal
      setIsGenerating(false); // Ensure loading state is reset on error

      // Karena gagal menyimpan atau generate, kita reset UI agar user bisa coba lagi.
      setViewMode("input"); // Kembali ke input mode pada error

      toast.error(
        "Gagal membuat atau menyimpan soal assessment. Silakan coba lagi.",
        { duration: 4000 }
      );
    } finally {
      // 6. CLEANUP AKHIR
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (generatedResult) {
      const rawContent = generatedTitle
        ? `## ${generatedTitle}\n\n${generatedResult}` // pakai heading markdown
        : generatedResult;

      // Konversi markdown ke HTML
      const htmlContent = marked(rawContent) as string;

      // Buat versi teks biasa (fallback)
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      const plainText = tempDiv.innerText;

      try {
        // Gunakan Clipboard API untuk menulis HTML + teks
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([htmlContent], { type: "text/html" }),
            "text/plain": new Blob([plainText], { type: "text/plain" }),
          }),
        ]);

        toast.success("Konten berhasil disalin dengan format!");
      } catch (err) {
        console.error("Clipboard error:", err);
        toast.error("Gagal menyalin dengan format");
      }
    }
  };

  const handleEdit = () => {
    if (generatedResult && generatedTitle) {
      setIsTransitioning(true);

      // Start fade out
      setTimeout(() => {
        setEditableResult(generatedResult);
        setEditableTitle(generatedTitle);
        if (!editingItemId && CurrentAssessmentId) {
          setEditingItemId(CurrentAssessmentId);
        }
        setViewMode("edit");

        // End transition after content change
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 150);
    }
  };

  const handleSaveEdit = async () => {
    if (!user?.id) {
      toast.error("User tidak terautentikasi.");
      return;
    }

    // Validasi semua field penting
    if (
      !editableTitle.trim() ||
      !editableResult.trim() ||
      !formData.mataPelajaran.trim() ||
      !formData.jenjangPendidikan.trim() ||
      !formData.topik.trim() ||
      !formData.tujuanPembelajaran.trim() ||
      !formData.tipePertanyaan.trim()
    ) {
      toast.error("Semua field wajib diisi!");
      return;
    }

    setIsTransitioning(true);

    // Pastikan selalu punya target ID (fallback ke CurrentAssessmentId)
    const targetId = editingItemId ?? CurrentAssessmentId;

    if (!targetId) {
      setIsTransitioning(false);
      toast.error(
        "ID assessment tidak ditemukan. Silakan simpan/generate terlebih dahulu."
      );
      return;
    }

    const loadingToast = toast.loading("Menyimpan perubahan...");
    try {
      const updateResult = await updateAssessment(user.id, targetId, {
        title: editableTitle.trim(),
        content: editableResult.trim(),
        mata_pelajaran: formData.mataPelajaran,
        jenjang_pendidikan: formData.jenjangPendidikan,
        topik_tujuan: `${formData.topik} - ${formData.tujuanPembelajaran}`,
        tipe_pertanyaan: formData.tipePertanyaan,
        kriteria_tambahan: formData.kriteriaTambahan,
      });

      toast.dismiss(loadingToast);

      if (updateResult.success) {
        // Sinkronkan state lokal agar UI konsisten
        setGeneratedResult(editableResult.trim());
        setGeneratedTitle(editableTitle.trim());

        toast.success("Soal assessment berhasil diperbarui!");
        navigate("/history", { replace: true });
      } else {
        toast.error(updateResult.error || "Gagal memperbarui soal assessment");
      }
    } catch (error) {
      console.error("Error updating assessment:", error);
      toast.dismiss(loadingToast);
      toast.error("Terjadi kesalahan saat memperbarui soal assessment");
    } finally {
      setIsTransitioning(false);
    }
  };

  const handleCancelEdit = () => {
    if (editingItemId) {
      // If editing from history, go back to history
      navigate("/history");
    } else {
      // If editing new content, just cancel edit mode
      setIsTransitioning(true);

      // Start fade out
      setTimeout(() => {
        setEditableResult("");
        setEditableTitle("");
        setViewMode("result");
        toast("Perubahan dibatalkan");

        // End transition after content change
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
      }, 150);
    }
  };

  const handleExport = async () => {
    if (generatedResult && generatedTitle) {
      try {
        await exportResultToPDF(generatedResult, generatedTitle);
        toast.success("PDF berhasil diunduh dengan format yang rapi!");
      } catch (error) {
        console.error("Error exporting PDF:", error);
        toast.error("Gagal mengekspor PDF");
      }
    }
  };

  const handleReGenerate = async () => {
    // Validate that we have the necessary form data
    if (
      !formData.mataPelajaran ||
      !formData.jenjangPendidikan ||
      !formData.topik ||
      !formData.tujuanPembelajaran ||
      !formData.tipePertanyaan
    ) {
      toast.error("Data form tidak lengkap untuk regenerasi");
      return;
    }

    // Show confirmation toast
    toast("Regenerating assessment with same parameters...", {
      duration: 2000,
    });

    // Track regeneration usage
    // Reuse existing generation logic while mempertahankan history satu entri
    await handleGenerateNow({ isRegenerate: true });
  };

  const handleInviteCollaborator = () => {
    setShowInviteCollaboratorDialog(true);
  };

  const handleCloseInviteCollaboratorDialog = () => {
    setShowInviteCollaboratorDialog(false);
  };

  const handleInviteFriend = async (friendId: string) => {
    // Pastikan assessment sudah disimpan dan memiliki ID
    if (!CurrentAssessmentId) {
      toast.error("Mohon simpan rencana pembelajaran terlebih dahulu.");
      return;
    }

    const { getCurrentUser } = await import("../../lib/authUtils");
    const { user: currentUser, error: authError } = await getCurrentUser();

    if (authError || !currentUser) {
      toast.error("Sesi tidak valid. Silakan login kembali.");
      return;
    }

    try {
      // 1. Dapatkan judul dokumen dari tabel assessments
      const { data: doc, error: docError } = await supabase
        .from("assessments")
        .select("title")
        .eq("id", CurrentAssessmentId)
        .single();

      if (docError) {
        throw docError;
      }

      // 2. Masukkan notifikasi ke tabel notifications secara langsung
      const { error: notifError } = await supabase
        .from("notifications")
        .insert({
          user_id: friendId, // ID user yang diundang (penerima notifikasi)
          type: "assessment_invite",
          title: "Undangan Kolaborasi Assessments Baru",
          content: `Anda diundang untuk berkolaborasi pada materi "${doc.title}"`,
          related_entity_id: CurrentAssessmentId, // ID Assessment
          related_entity_type: "assessment",
          action_status: "pending", // Status awal undangan
          metadata: {
            inviter_id: currentUser.id,
          },
        });

      if (notifError) {
        throw notifError;
      }

      toast.success("Undangan kolaborasi berhasil dikirim!");
      return true;
    } catch (error) {
      console.error("Terjadi kesalahan saat mengundang kolaborator:", error);
      toast.error(
        "Gagal mengirim undangan kolaborasi. Periksa konsol untuk detailnya."
      );
      return false;
    }
  };

  const isFormValid =
    formData.mataPelajaran.trim() !== "" &&
    formData.jenjangPendidikan.trim() !== "" &&
    formData.kelas.trim() !== "" &&
    formData.topik.trim() !== "" &&
    formData.tujuanPembelajaran.trim() !== "" &&
    formData.tipePertanyaan.trim() !== "";

  return (
    <div className="bg-[#eeeff6] w-full min-h-screen flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Main Content with top padding to account for fixed navbar */}
      <main className="pt-[70px] px-4 sm:px-6 pb-6 flex-1">
        <div className="w-full mx-auto mt-8 bg-white rounded-xl border-b border-[#f0eff0] relative p-4 sm:p-8 min-h-[700px] overflow-hidden">
          {/* Background decorative elements */}
          <div className="w-[328px] top-1 left-[-286px] bg-[#ffea9326] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />
          <div className="w-[328px] top-0 left-[380px] bg-[#f7f8ff] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />
          <div className="w-[328px] top-0 left-[790px] bg-[#f7f8ff] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />
          <div className="w-[329px] top-0 left-[62px] bg-[#eba0ff12] rounded-[164.67px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />

          {/* Content Container - Always render both columns with transitions */}
          <div className="relative z-10 h-full">
            {/* Header with back button and history button */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleBackToDashboard}
                  variant="ghost"
                  className="flex items-center gap-2 text-[#373e46] hover:bg-gray-100 p-2 rounded-lg w-auto"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </Button>
                <span className="font-medium text-base sm:text-lg text-[#373e46]">
                  {editingItemId ? "Edit Assessment" : "Assessment Builder"}
                </span>
              </div>

              {/* History Button */}
              <Button
                onClick={handleGoToHistory}
                variant="outline"
                className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-xl font-medium transition-all duration-300"
              >
                <HistoryIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Riwayat</span>
              </Button>
            </div>

            {/* Main Layout Container with smooth transitions */}
            <div className="flex gap-4 sm:gap-8 h-full transition-all duration-500 ease-in-out flex-col lg:flex-row">
              {/* Left Column - Form or Read-only Result */}
              <div
                className={`transition-all duration-500 ease-in-out ${
                  viewMode === "input"
                    ? "w-full max-w-2xl mx-auto"
                    : "w-full lg:w-[500px] lg:flex-shrink-0"
                }`}
              >
                {viewMode === "edit" ? (
                  /* Edit Mode - Show read-only result on left */
                  <div className="h-full flex flex-col">
                    <h2 className="text-lg sm:text-xl font-semibold text-[#373e46] mb-4 sm:mb-6">
                      Result
                    </h2>
                    {isGenerating ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <GenerateLoadingModal isOpen={true} />
                      </div>
                    ) : (
                      <div className="overflow-y-auto p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4 sm:mb-6 max-h-[400px] sm:max-h-[500px]">
                        {generatedTitle && (
                          <h3 className="text-lg sm:text-xl font-bold text-[#373e46] mb-4 pb-3 border-b border-gray-300">
                            {generatedTitle}
                          </h3>
                        )}
                        <div className="relative">
                          <MarkdownRenderer
                            content={generatedResult || ""}
                            className="text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Form - Always rendered */
                  <AssessmentForm
                    formData={formData}
                    handleInputChange={handleInputChange}
                    isFormValid={isFormValid}
                    isGenerating={isGenerating}
                    progress={progress}
                    handleGenerateNow={handleGenerateNow}
                    handleEnhancePrompt={handleEnhancePrompt}
                  />
                )}
              </div>

              {/* Right Column - Result/Loading/Edit - Only visible when not in input mode */}
              <div
                className={`transition-all duration-500 ease-in-out min-h-[400px] sm:min-h-[600px] flex flex-col ${
                  viewMode === "input"
                    ? "w-0 opacity-0 overflow-hidden lg:block hidden"
                    : "flex-1 opacity-100"
                }`}
              >
                {viewMode === "result" && isGenerating ? (
                  /* Show loading modal when generating */
                  <div className="w-full h-full flex items-center justify-center">
                    <GenerateLoadingModal isOpen={true} />
                  </div>
                ) : viewMode === "result" &&
                  (generatedResult || isStreaming || streamedContent) ? (
                  <div
                    className={`w-full flex flex-col h-full transition-all duration-300 ease-in-out ${
                      isTransitioning
                        ? "opacity-0 transform translate-x-4"
                        : "opacity-100 transform translate-x-0"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <div className="flex items-center gap-3">
                        <h2 className="text-lg sm:text-xl font-semibold text-[#373e46]">
                          Result
                        </h2>
                        {/* Streaming indicators removed */}
                      </div>
                    </div>

                    <div className="overflow-y-auto p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4 sm:mb-6 max-h-[400px] sm:max-h-[500px]">
                      {generatedTitle && (
                        <h3 className="text-lg sm:text-xl font-bold text-[#373e46] mb-4 pb-3 border-b border-gray-300">
                          {generatedTitle}
                        </h3>
                      )}
                      <div className="relative">
                        <MarkdownRenderer
                          content={generatedResult || ""}
                          className="text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    {/* Re-Generate Button - Full width with Figma design */}
                    <div className="mb-4">
                      <Button
                        onClick={handleReGenerate}
                        disabled={isGenerating}
                        className="w-full h-12 rounded-[24px] bg-[#EFE8FF] border-2 border-transparent bg-clip-padding relative overflow-hidden transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background:
                            "linear-gradient(#EFE8FF, #EFE8FF) padding-box, linear-gradient(90deg, #6A2CFE 0%, #010FFE 100%) border-box",
                        }}
                      >
                        <div className="flex items-center justify-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-[#6A2CFE] to-[#010FFE] font-semibold text-base">
                          <RefreshCwIcon
                            className={`w-5 h-5 text-[#6A2CFE] ${
                              isGenerating ? "animate-spin" : ""
                            }`}
                          />
                          Regenerate
                        </div>
                      </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        onClick={handleExport}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 h-10 sm:h-auto"
                      >
                        <DownloadIcon className="w-4 h-4" />
                        Export
                      </Button>
                      <Button
                        onClick={handleEdit}
                        className="flex items-center justify-center gap-2 h-10 sm:h-auto hover:opacity-90 hover:scale-105 transition-all duration-300"
                        style={{
                          background: "#EEEEF3",
                          color: "#37363E",
                        }}
                      >
                        <EditIcon className="w-4 h-4" />
                        Edit
                      </Button>
                      <Button
                        onClick={handleInviteCollaborator}
                        className="flex items-center justify-center gap-2 h-10 sm:h-auto hover:opacity-90 hover:scale-105 transition-all duration-300"
                        style={{
                          background: "transparent",
                          color: "#6b2cfe",
                          border: "1px solid #6b2cfe",
                        }}
                      >
                        <UsersIcon className="w-4 h-4" />
                        Undang Kolaborasi
                      </Button>
                    </div>

                    {/* TAMBAHAN: Comment Section */}
                    {CurrentAssessmentId && (
                      <CommentSectionAssessment
                        assessmentId={CurrentAssessmentId}
                      />
                    )}
                  </div>
                ) : viewMode === "edit" ? (
                  <div
                    className={`w-full flex flex-col h-full transition-all duration-300 ease-in-out ${
                      isTransitioning
                        ? "opacity-0 transform translate-x-4"
                        : "opacity-100 transform translate-x-0"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <h2 className="text-lg sm:text-xl font-semibold text-[#373e46]">
                        Edit Soal
                      </h2>

                      <Button
                        onClick={handleCancelEdit}
                        variant="ghost"
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <XIcon className="w-5 h-5 text-gray-500" />
                      </Button>
                    </div>

                    <div className="flex-1 flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
                      {/* Editable Title */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-[#373e46]">
                          Judul
                        </label>
                        <Input
                          value={editableTitle}
                          onChange={e => setEditableTitle(e.target.value)}
                          className="h-10 sm:h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white"
                          placeholder="Masukkan judul"
                        />
                      </div>

                      <div className="space-y-2 flex-1 flex flex-col">
                        <label className="block text-sm font-medium text-[#373e46]">
                          Konten Soal
                        </label>
                        <Textarea
                          value={editableResult}
                          onChange={e => setEditableResult(e.target.value)}
                          className="flex-1 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white min-h-[300px] sm:min-h-[400px]"
                          placeholder="Masukkan konten soal"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <Button
                        onClick={handleReGenerate}
                        disabled={isGenerating}
                        className="w-full h-12 rounded-[24px] bg-[#EFE8FF] border-2 border-transparent bg-clip-padding relative overflow-hidden transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          background:
                            "linear-gradient(#EFE8FF, #EFE8FF) padding-box, linear-gradient(90deg, #6A2CFE 0%, #010FFE 100%) border-box",
                        }}
                      >
                        <div className="flex items-center justify-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-[#6A2CFE] to-[#010FFE] font-semibold text-base">
                          <RefreshCwIcon
                            className={`w-5 h-5 text-[#6A2CFE] ${
                              isGenerating ? "animate-spin" : ""
                            }`}
                          />
                          Regenerate
                        </div>
                      </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                      <Button
                        onClick={handleCopy}
                        variant="outline"
                        className="flex items-center justify-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 h-10 sm:h-auto"
                      >
                        <CopyIcon className="w-4 h-4" />
                        Copy
                      </Button>
                      <Button
                        onClick={handleExport}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 h-10 sm:h-auto"
                      >
                        <DownloadIcon className="w-4 h-4" />
                        Export
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        className="flex items-center justify-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 h-10 sm:h-auto"
                      >
                        <XIcon className="w-4 h-4" />
                        {editingItemId ? "Kembali" : "Cancel"}
                      </Button>

                      <Button
                        onClick={handleSaveEdit}
                        // ⚙️ MODIFIKASI: Tambahkan properti `disabled`
                        disabled={isCommentsLoading || isTransitioning}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg transition-all duration-300 h-10 sm:h-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckIcon className="w-4 h-4" />
                        {editingItemId ? "Update" : "Save Changes"}
                      </Button>
                      <Button
                        onClick={handleInviteCollaborator}
                        className="flex items-center justify-center gap-2 h-10 sm:h-auto hover:opacity-90 hover:scale-105 transition-all duration-300"
                        style={{
                          background: "transparent",
                          color: "#6b2cfe",
                          border: "1px solid #6b2cfe",
                        }}
                      >
                        <UsersIcon className="w-4 h-4" />
                        Undang Kolaborasi
                      </Button>
                    </div>
                    {/* TAMBAHAN: Comment Section di Edit Mode */}
                    {CurrentAssessmentId && (
                      <CommentSectionAssessment
                        assessmentId={CurrentAssessmentId}
                        // ⚙️ MODIFIKASI: Berikan fungsi callback `onLoadComplete`
                        onLoadComplete={() => setIsCommentsLoading(false)}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Enhance Prompt Dialog */}
      <EnhancePromptDialog
        isOpen={showEnhancePromptDialog}
        onClose={handleCloseEnhancePromptDialog}
        currentPrompt={`${formData.topik} - ${formData.tujuanPembelajaran}`}
        currentMataPelajaran={formData.mataPelajaran}
        currentJenjangPendidikan={formData.jenjangPendidikan}
        currentTipePertanyaan={formData.tipePertanyaan}
        currentKelas={formData.kelas}
        onAccept={handleAcceptEnhancedPrompt}
      />

      {/* Invite Collaborator Dialog */}
      <InviteCollaboratorDialog
        isOpen={showInviteCollaboratorDialog}
        onClose={handleCloseInviteCollaboratorDialog}
        onInvite={handleInviteFriend}
      />
    </div>
  );
};
